import React, { useState } from 'react';
import { Layout } from '../Layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Select from '@cloudscape-design/components/select';
import { eventsList, gradesList } from 'src/utils/constant';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { getApiHeader } from 'src/utils';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';

interface EventInformation {
    ParticipantID: string;
    IndividualChestNum: string;
    GroupChestNum: string;
    FirstName: string;
    MiddleName: string;
    LastName: string;
    GradeCategory: string;
    ParticipantPhoneNumber: string;
    GuardianPhoneNumber: string;
    GuardianEmail: string;
    IsCheckedIn: string;
    CheckedinDateTime: string;
    EventType: string;
    CategoryName: string;
    EventAgeGradeSequence: string;
    EventAgeGrade: string;
    EventName: string;
    EventPlannedStartDatetime: string;
    EventActualStartDatetime: string;
    EventActualEndDatetime: string;
    EventStatus: string;
    StageID: string;
    StageName: string;
    ParticipantPlannedStartDatetime: string;
    ParticipantStatus: string;
}

export const EventInformation: React.FC = () => {
    const [selectedEvent, setSelectedEvent] = useState<OptionDefinition>({ label: 'Select event', value: '' });
    const [selectedGrade, setSelectedGrade] = useState<OptionDefinition>({ label: 'Select grade', value: '' });
    const [selectedEventCategory, setSelectedEventCategory] = useState('');
    const [validationError, setValidationError] = useState('');

    const [enableGetEventInformation, setEnableGetEventInformation] = useState(false);

    const headers = getApiHeader();

    const { data: eventInformation, isLoading, error } = useQuery({
        queryKey: [`getEventInformation-${selectedEventCategory}-${selectedGrade.value}`],
        queryFn: async () => {
            const result = await axios.post('https://q86fcfsskf.execute-api.us-east-1.amazonaws.com/Prod/participantforevent',
                {
                    eventCategory: selectedEventCategory,
                    Grade: selectedGrade.value,
                },
                {
                    headers
                });
            return result?.data?.result as EventInformation[];
        },
        enabled: enableGetEventInformation,
        staleTime: 0,
    });

    const reset = () => {
        setSelectedEvent({ label: 'Select event', value: '' });
        setSelectedGrade({ label: 'Select grade', value: '' });
        setEnableGetEventInformation(false);
    };

    const searchEventInformation = () => {
        if (!selectedEvent.value || !selectedGrade.value || !selectedEventCategory) {
            setValidationError('Please select event category and grade to continue');
            setEnableGetEventInformation(false);
        } else {
            setEnableGetEventInformation(true);
            setValidationError('');
        }
    }

    return (
        <Layout breadcrumbs={[
            {
                href: '/event-information',
                text: 'Event information'
            }
        ]}>
            <SpaceBetween direction="vertical" size="m">
                {
                    (validationError || error) && (
                        <Alert type='error'>{validationError || error?.message}</Alert>
                    )
                }
                <ExpandableSection
                    defaultExpanded
                    variant="container"
                    headerText="Search Events"
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Form
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button formAction="none" variant="link" onClick={reset}>
                                        Clear
                                    </Button>
                                    <Button variant="primary" onClick={searchEventInformation} loading={isLoading}>Search</Button>
                                </SpaceBetween>
                            }
                        >
                            <SpaceBetween direction="horizontal" size="l">
                                <FormField label="Event category">
                                    <Select
                                        selectedOption={selectedEvent}
                                        onChange={({ detail }) => {
                                            setSelectedEvent(detail.selectedOption);
                                            setSelectedEventCategory(detail.selectedOption.label!);
                                            setValidationError('');
                                            setEnableGetEventInformation(false);
                                        }}
                                        options={eventsList.map((event) => ({
                                            label: event.name, value: event.id
                                        }))}
                                    />
                                </FormField>
                                <FormField label="Grade">
                                    <Select
                                        selectedOption={selectedGrade}
                                        onChange={({ detail }) => {
                                            setSelectedGrade(detail.selectedOption);
                                            setValidationError('');
                                            setEnableGetEventInformation(false);
                                        }
                                        }
                                        options={gradesList.map((grade) => ({
                                            label: grade, value: grade
                                        }))} />
                                </FormField>
                            </SpaceBetween>
                        </Form>
                    </SpaceBetween>
                </ExpandableSection>
                {
                    eventInformation && (
                        <ExpandableSection defaultExpanded
                            variant="container"
                            headerText="Events details">
                            <Table
                                columnDefinitions={[
                                    {
                                        id: "chestNumber",
                                        header: "Chest number",
                                        cell: item => item.IndividualChestNum || item.GroupChestNum || "-",
                                        sortingField: "alt"
                                    },
                                    {
                                        id: "phoneNumber",
                                        header: "Guardian Phone",
                                        cell: item => item.GuardianPhoneNumber || "-"
                                    },
                                    {
                                        id: "phoneNumber",
                                        header: "Partcipant Phone",
                                        cell: item => item.ParticipantPhoneNumber || "-"
                                    },
                                    {
                                        id: "eventCategory",
                                        header: "Event category",
                                        cell: item => item.EventName || "-"
                                    },
                                    {
                                        id: "grade",
                                        header: "Grade",
                                        cell: item => item.EventAgeGrade || "-"
                                    },
                                    {
                                        id: "timeSlot",
                                        header: "Time slot",
                                        cell: item => item.ParticipantPlannedStartDatetime || "-"
                                    },
                                    {
                                        id: "checkedIn",
                                        header: "Check-in",
                                        cell: item => item.IsCheckedIn ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>
                                    }
                                ]}
                                items={eventInformation}
                                loadingText="Loading resources"
                                sortingDisabled
                                empty={
                                    <Box
                                        margin={{ vertical: "xs" }}
                                        textAlign="center"
                                        color="inherit"
                                    >
                                        <SpaceBetween size="m">
                                            <b>No details found</b>
                                        </SpaceBetween>
                                    </Box>
                                }
                            />
                        </ExpandableSection>
                    )
                }
            </SpaceBetween>
        </Layout>
    )
}